/**
 * Admin API hooks (TanStack Query) — GDEV-63.
 *
 * Backend: backend/app/api/v1/admin.py (require_role("admin") 라우터 레벨 가드)
 *
 * 특징:
 *  - 응답은 `ApiResponse<T>` 래퍼 없이 직접 body (관리자 라우터 규약)
 *  - System/Usage 모니터링은 자동 폴링 (10s / 30s)
 *  - Mutation 성공 시 관련 list + detail query 만 선택적 무효화
 */

import { apiClient } from '@/lib/api/client'
import type { ApiError } from '@/lib/api/types'
import type {
  AdminActivitySummary,
  AdminUserDetail,
  AdminUserListResponse,
  AdminUsersFilters,
  ApprovalFilters,
  ApprovalPendingListResponse,
  ApprovalReviewResponse,
  ApprovalTargetType,
  AuditLogFilters,
  AuditLogListResponse,
  ContentModerationListResponse,
  ContentModerationRequest,
  ContentModerationResponse,
  FeatureFlagsStatus,
  FeatureFlagsUpdate,
  ModerationFilters,
  OperationLimitsStatus,
  OperationLimitsUpdate,
  PublicSettingsStatus,
  PublicSettingsUpdate,
  RegistrationReviewRequest,
  RoleUpdateRequest,
  SuspendRequest,
  SystemMonitoring,
  TransferLimitsStatus,
  TransferLimitsUpdate,
  UsageMonitoring,
} from '@/types/admin'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

// ── Query Keys ──────────────────────────────────────────────────────────────

export const adminKeys = {
  all: ['admin'] as const,
  users: () => [...adminKeys.all, 'users'] as const,
  userList: (filters: AdminUsersFilters) => [...adminKeys.users(), 'list', filters] as const,
  userDetail: (userId: string) => [...adminKeys.users(), 'detail', userId] as const,
  moderation: () => [...adminKeys.all, 'moderation'] as const,
  moderationList: (filters: ModerationFilters) =>
    [...adminKeys.moderation(), 'list', filters] as const,
  approvals: () => [...adminKeys.all, 'approvals'] as const,
  approvalList: (filters: ApprovalFilters) => [...adminKeys.approvals(), 'list', filters] as const,
  monitoring: () => [...adminKeys.all, 'monitoring'] as const,
  monitoringSystem: () => [...adminKeys.monitoring(), 'system'] as const,
  monitoringUsage: () => [...adminKeys.monitoring(), 'usage'] as const,
  auditLogs: () => [...adminKeys.all, 'audit-logs'] as const,
  auditLogList: (filters: AuditLogFilters) => [...adminKeys.auditLogs(), 'list', filters] as const,
  settings: () => [...adminKeys.all, 'settings'] as const,
  featureFlags: () => [...adminKeys.settings(), 'feature-flags'] as const,
  operationLimits: () => [...adminKeys.settings(), 'operation-limits'] as const,
  transferLimits: () => [...adminKeys.settings(), 'transfer-limits'] as const,
  publicSettings: () => [...adminKeys.settings(), 'public'] as const,
}

// ── AD-MM: 회원 관리 ────────────────────────────────────────────────────────

/** 사용자 목록 (필터/페이지네이션). */
export function useAdminUsers(filters: AdminUsersFilters = {}) {
  const params: Record<string, string | number | boolean | undefined | null> = {
    role: filters.role,
    search: filters.search || undefined,
    is_active: filters.is_active,
    registration_status: filters.registration_status,
    page: filters.page ?? 1,
    page_size: filters.page_size ?? 20,
  }
  return useQuery<AdminUserListResponse, ApiError>({
    queryKey: adminKeys.userList(filters),
    queryFn: () => apiClient.get<AdminUserListResponse>('/admin/users', { params }),
  })
}

/** 사용자 상세. */
export function useAdminUser(userId: string) {
  return useQuery<AdminUserDetail, ApiError>({
    queryKey: adminKeys.userDetail(userId),
    queryFn: () => apiClient.get<AdminUserDetail>(`/admin/users/${userId}`),
    enabled: !!userId,
  })
}

/** 역할 변경. */
export function useUpdateUserRole() {
  const qc = useQueryClient()
  return useMutation<AdminUserDetail, ApiError, { userId: string; body: RoleUpdateRequest }>({
    mutationFn: ({ userId, body }) =>
      apiClient.patch<AdminUserDetail>(`/admin/users/${userId}/role`, body),
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: adminKeys.users() })
      qc.invalidateQueries({ queryKey: adminKeys.userDetail(vars.userId) })
      qc.invalidateQueries({ queryKey: adminKeys.auditLogs() })
    },
  })
}

/** 사용자 정지. */
export function useSuspendUser() {
  const qc = useQueryClient()
  return useMutation<AdminUserDetail, ApiError, { userId: string; body: SuspendRequest }>({
    mutationFn: ({ userId, body }) =>
      apiClient.post<AdminUserDetail>(`/admin/users/${userId}/suspend`, body),
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: adminKeys.users() })
      qc.invalidateQueries({ queryKey: adminKeys.userDetail(vars.userId) })
      qc.invalidateQueries({ queryKey: adminKeys.auditLogs() })
    },
  })
}

/** 사용자 정지 해제. */
export function useUnsuspendUser() {
  const qc = useQueryClient()
  return useMutation<AdminUserDetail, ApiError, { userId: string }>({
    mutationFn: ({ userId }) => apiClient.post<AdminUserDetail>(`/admin/users/${userId}/unsuspend`),
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: adminKeys.users() })
      qc.invalidateQueries({ queryKey: adminKeys.userDetail(vars.userId) })
      qc.invalidateQueries({ queryKey: adminKeys.auditLogs() })
    },
  })
}

function useReviewRegistration(decision: 'approve' | 'reject') {
  const qc = useQueryClient()
  return useMutation<
    AdminUserDetail,
    ApiError,
    { userId: string; body: RegistrationReviewRequest }
  >({
    mutationFn: ({ userId, body }) =>
      apiClient.post<AdminUserDetail>(`/admin/users/${userId}/${decision}`, body),
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: adminKeys.users() })
      qc.invalidateQueries({ queryKey: adminKeys.userDetail(vars.userId) })
      qc.invalidateQueries({ queryKey: adminKeys.auditLogs() })
    },
  })
}

export function useApproveRegistration() {
  return useReviewRegistration('approve')
}

export function useRejectRegistration() {
  return useReviewRegistration('reject')
}

// ── AD-AM-04: 모더레이션 ────────────────────────────────────────────────────

export function useModerations(filters: ModerationFilters = {}) {
  const params: Record<string, string | number | undefined | null> = {
    target_type: filters.target_type,
    page: filters.page ?? 1,
    page_size: filters.page_size ?? 20,
  }
  return useQuery<ContentModerationListResponse, ApiError>({
    queryKey: adminKeys.moderationList(filters),
    queryFn: () => apiClient.get<ContentModerationListResponse>('/admin/moderation', { params }),
  })
}

export function useCreateModeration() {
  const qc = useQueryClient()
  return useMutation<ContentModerationResponse, ApiError, ContentModerationRequest>({
    mutationFn: (body) => apiClient.post<ContentModerationResponse>('/admin/moderation', body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: adminKeys.moderation() })
      qc.invalidateQueries({ queryKey: adminKeys.auditLogs() })
    },
  })
}

// ── H-18: 산출물 등록 승인 ──────────────────────────────────────────────────

/** 승인 대기 목록. 데이터셋과 모델을 한 화면에서 본다. */
export function useApprovals(filters: ApprovalFilters = {}) {
  const params: Record<string, string | number | undefined | null> = {
    target_type: filters.target_type,
    status: filters.status ?? 'pending',
    page: filters.page ?? 1,
    size: filters.size ?? 20,
  }
  return useQuery<ApprovalPendingListResponse, ApiError>({
    queryKey: adminKeys.approvalList(filters),
    queryFn: () => apiClient.get<ApprovalPendingListResponse>('/admin/approvals', { params }),
  })
}

/**
 * 승인 또는 거절.
 *
 * 카탈로그 목록도 함께 무효화한다 — 승인은 그 자료가 목록에 나타난다는 뜻이라
 * 승인 화면만 갱신하면 카탈로그가 옛 상태로 남는다.
 */
export function useReviewApproval() {
  const qc = useQueryClient()
  return useMutation<
    ApprovalReviewResponse,
    ApiError,
    {
      targetType: ApprovalTargetType
      targetId: string
      decision: 'approve' | 'reject'
      reason?: string
    }
  >({
    mutationFn: ({ targetType, targetId, decision, reason }) =>
      apiClient.post<ApprovalReviewResponse>(
        `/admin/approvals/${targetType}/${targetId}/${decision}`,
        { reason: reason ?? null },
      ),
    onSuccess: (_data, variables) => {
      qc.invalidateQueries({ queryKey: adminKeys.approvals() })
      qc.invalidateQueries({ queryKey: adminKeys.auditLogs() })
      qc.invalidateQueries({
        queryKey: variables.targetType === 'dataset' ? ['datasets'] : ['models'],
      })
    },
  })
}

// ── AD-MO: 모니터링 ─────────────────────────────────────────────────────────

/** 시스템 자원 스냅샷. 10초 간격 자동 갱신. */
export function useSystemMonitoring() {
  return useQuery<SystemMonitoring, ApiError>({
    queryKey: adminKeys.monitoringSystem(),
    queryFn: () => apiClient.get<SystemMonitoring>('/admin/monitoring/system'),
    refetchInterval: 10_000,
    refetchIntervalInBackground: false,
  })
}

/** 사용량 통계. 30초 간격 자동 갱신. */
export function useUsageMonitoring() {
  return useQuery<UsageMonitoring, ApiError>({
    queryKey: adminKeys.monitoringUsage(),
    queryFn: () => apiClient.get<UsageMonitoring>('/admin/monitoring/usage'),
    refetchInterval: 30_000,
    refetchIntervalInBackground: false,
  })
}

/** 관리자 본인 활동 요약 (GDEV-326) — audit_logs 실집계. */
export function useMyActivity() {
  return useQuery<AdminActivitySummary, ApiError>({
    queryKey: [...adminKeys.all, 'me', 'activity'] as const,
    queryFn: () => apiClient.get<AdminActivitySummary>('/admin/me/activity'),
  })
}

/** 감사 로그 (manual refetch). */
export function useAuditLogs(filters: AuditLogFilters = {}) {
  const params: Record<string, string | number | undefined | null> = {
    source: filters.source,
    page: filters.page ?? 1,
    page_size: filters.page_size ?? 50,
  }
  return useQuery<AuditLogListResponse, ApiError>({
    queryKey: adminKeys.auditLogList(filters),
    queryFn: () => apiClient.get<AuditLogListResponse>('/admin/audit-logs', { params }),
  })
}

// ── 플랫폼 설정 ─────────────────────────────────────────────────────────────

/** 현재 기능 On/Off 상태. */
export function useFeatureFlagsAdmin() {
  return useQuery<FeatureFlagsStatus, ApiError>({
    queryKey: adminKeys.featureFlags(),
    queryFn: () => apiClient.get<FeatureFlagsStatus>('/admin/settings/features'),
    retry: false,
  })
}

/** 기능 On/Off 변경. */
export function useUpdateFeatureFlagsAdmin() {
  const qc = useQueryClient()
  return useMutation<FeatureFlagsStatus, ApiError, FeatureFlagsUpdate>({
    mutationFn: (body) => apiClient.put<FeatureFlagsStatus>('/admin/settings/features', body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: adminKeys.featureFlags() })
      qc.invalidateQueries({ queryKey: adminKeys.auditLogs() })
    },
  })
}

export function usePublicSettingsAdmin() {
  return useQuery<PublicSettingsStatus, ApiError>({
    queryKey: adminKeys.publicSettings(),
    queryFn: () => apiClient.get<PublicSettingsStatus>('/admin/settings/public'),
    retry: false,
  })
}

export function useUpdatePublicSettingsAdmin() {
  const qc = useQueryClient()
  return useMutation<PublicSettingsStatus, ApiError, PublicSettingsUpdate>({
    mutationFn: (body) => apiClient.put<PublicSettingsStatus>('/admin/settings/public', body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: adminKeys.publicSettings() })
      qc.invalidateQueries({ queryKey: adminKeys.auditLogs() })
    },
  })
}

export function useOperationLimitsAdmin() {
  return useQuery<OperationLimitsStatus, ApiError>({
    queryKey: adminKeys.operationLimits(),
    queryFn: () => apiClient.get<OperationLimitsStatus>('/admin/settings/limits'),
    retry: false,
  })
}

export function useUpdateOperationLimitsAdmin() {
  const qc = useQueryClient()
  return useMutation<OperationLimitsStatus, ApiError, OperationLimitsUpdate>({
    mutationFn: (body) => apiClient.put<OperationLimitsStatus>('/admin/settings/limits', body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: adminKeys.operationLimits() })
      qc.invalidateQueries({ queryKey: adminKeys.auditLogs() })
    },
  })
}

export function useTransferLimitsAdmin() {
  return useQuery<TransferLimitsStatus, ApiError>({
    queryKey: adminKeys.transferLimits(),
    queryFn: () => apiClient.get<TransferLimitsStatus>('/admin/settings/transfer-limits'),
    retry: false,
  })
}

export function useUpdateTransferLimitsAdmin() {
  const qc = useQueryClient()
  return useMutation<TransferLimitsStatus, ApiError, TransferLimitsUpdate>({
    mutationFn: (body) =>
      apiClient.put<TransferLimitsStatus>('/admin/settings/transfer-limits', body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: adminKeys.transferLimits() })
      qc.invalidateQueries({ queryKey: adminKeys.auditLogs() })
    },
  })
}
